/**
 * geo-locale.js — Avnzor
 *
 * Geo-based default language detection.
 * Switches to Arabic for Saudi visitors (or when geo is unavailable) unless
 * the user has previously made a manual language selection.
 *
 * Acceptance criteria covered:
 *  1. Saudi IP → Arabic on load
 *  2. First-time visit, no stored pref → Arabic
 *  3. Returning user with manual pref → respected (no auto-switch)
 *  4. Manual switch → persisted for 1 year via cookie
 *  5. Geo fails / country unknown → Arabic (global default)
 *  6. Navigation between pages → Weglot maintains choice natively
 */
(function () {
  'use strict';

  // Cookie name that records a deliberate user language choice.
  // Only set when the user clicks the language switcher — never on auto-switch.
  var MANUAL_PREF_COOKIE = 'avnzor_lang_manual';

  // Countries that should default to Arabic.
  // SA = Saudi Arabia. Add 'AE', 'KW', etc. here if needed in future.
  var ARABIC_COUNTRIES = ['SA'];

  // ── Cookie helpers ──────────────────────────────────────────────────────

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() +
      '; path=/; SameSite=Lax';
  }

  // ── Manual-preference helpers ───────────────────────────────────────────

  function hasManualPref() {
    return getCookie(MANUAL_PREF_COOKIE) === '1';
  }

  function markManualPref() {
    setCookie(MANUAL_PREF_COOKIE, '1', 365); // Persist for 1 year
  }

  // ── Core logic ──────────────────────────────────────────────────────────

  // Flag set to true while we are programmatically calling Weglot.switchTo(),
  // so the languageChanged listener does not treat it as a manual action.
  var isAutoSwitching = false;

  function applyGeoLanguage(countryCode) {
    // Criteria 3: if user already has a manual preference, do nothing.
    if (hasManualPref()) return;

    // Criteria 1 + 5: switch to Arabic when country is SA *or* unknown/empty.
    // An unknown/empty code means geo detection failed → fall back to Arabic.
    var country = (countryCode || '').toUpperCase();
    var shouldUseArabic = !country || ARABIC_COUNTRIES.indexOf(country) !== -1;
    if (!shouldUseArabic) return;

    // Nothing to do if already in Arabic.
    var currentLang =
      typeof Weglot.getCurrentLang === 'function'
        ? Weglot.getCurrentLang()
        : 'en';
    if (currentLang === 'ar') return;

    // Perform the auto-switch.
    isAutoSwitching = true;
    Weglot.switchTo('ar');
    // isAutoSwitching is cleared inside the languageChanged listener below.
  }

  function attachManualPrefListener() {
    if (typeof Weglot.on !== 'function') return;

    Weglot.on('languageChanged', function () {
      if (isAutoSwitching) {
        // This event was triggered by our own switchTo() call — not the user.
        isAutoSwitching = false;
        return;
      }
      // The user clicked the language switcher → record their preference.
      // Criteria 4: this cookie causes future visits to skip geo auto-switch.
      markManualPref();
    });
  }

  function init(countryCode) {
    // Attach listener first so any auto-switch event is properly flagged.
    attachManualPrefListener();
    // Apply geo language (no-op if manual pref or already correct language).
    applyGeoLanguage(countryCode);
  }

  // ── Wait for Weglot to be ready ─────────────────────────────────────────

  function run() {
    var countryCode = document.documentElement.getAttribute('data-country') || '';
    init(countryCode);
  }

  if (window.Weglot && typeof Weglot.getCurrentLang === 'function') {
    // Weglot is already initialised (e.g., script loaded synchronously).
    run();
  } else {
    // Listen for the Weglot ready event.
    document.addEventListener('weglot:initialized', function () { run(); }, {
      once: true,
    });
    // Fallback poll in case the event already fired before our listener.
    var attempts = 0;
    var poll = setInterval(function () {
      if (window.Weglot && typeof Weglot.getCurrentLang === 'function') {
        clearInterval(poll);
        run();
      }
      if (++attempts > 100) clearInterval(poll); // Give up after ~10 s
    }, 100);
  }
})();
